/**
 * API service for exam-related operations using HATEOAS architecture
 */
import axios from 'axios';
import { apiRequest } from '../apiInterceptor';
import debugExamApi from './debugExamApi';

// Debug tracking for API calls
const apiCallTracker = {
  _calls: [],
  _lastTenCalls: [],
  callCount: {},
  
  trackCall(url, method, requestId) {
    const shortUrl = url.split('?')[0]; // Remove query params for tracking
    const key = `${method}:${shortUrl}`;
    this.callCount[key] = (this.callCount[key] || 0) + 1;
    
    const callInfo = {
      timestamp: Date.now(),
      requestId,
      url,
      method,
      count: this.callCount[key],
      source: new Error().stack
    };
    
    this._calls.push(callInfo);
    
    // Keep last 10 calls for easy debugging
    this._lastTenCalls.push(callInfo);
    if (this._lastTenCalls.length > 10) {
      this._lastTenCalls.shift();
    }
    
    // Check for potential duplicate calls (same URL called multiple times within 100ms)
    const recentCalls = this._calls.filter(
      call => call.url === url && 
      call.method === method && 
      call.timestamp > Date.now() - 100
    );
    
    if (recentCalls.length > 1) {
      // console.warn(`ExamAPI: Potential duplicate API call detected! ${method} ${url} called ${recentCalls.length} times in the last 100ms!`, {
      //   recentCalls,
      //   currentStack: new Error().stack.split('\n').slice(0, 5).join('\n'),
      //   lastTenCalls: this._lastTenCalls
      // });
    }
    
    return callInfo;
  },
  
  getCallCount(url, method) {
    const shortUrl = url.split('?')[0];
    const key = `${method}:${shortUrl}`;
    return this.callCount[key] || 0;
  }
};

// Expose the API call tracker for debugging
window._apiCallTracker = apiCallTracker;

// Base API configuration
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Set authorization header for authenticated requests
const setAuthHeader = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

/**
 * Start a new practice exam session
 * @param {Object} examConfig - Configuration for the exam
 * @returns {Promise} - Promise with exam session data
 */
const startExam = async (examConfig) => {
  const requestId = Math.random().toString(36).substring(2, 10);
  const url = '/api/exam/start';
  
  try {
    apiCallTracker.trackCall(url, 'POST', requestId);
   // console.log(`ExamAPI [${requestId}]: Starting exam session with config:`, examConfig);
      const startTime = Date.now();
    const response = await apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(examConfig)
    });
    const duration = Date.now() - startTime;
    
   // console.log(`ExamAPI [${requestId}]: Exam session created in ${duration}ms, examId: ${response?.id || 'unknown'}`);
    return response;
  } catch (error) {
    //console.error(`ExamAPI [${requestId}]: Error starting exam:`, error);
    throw error;
  }
};

/**
 * Get or post resources by following HATEOAS link
 * @param {string} link - HATEOAS link to follow
 * @param {string} method - HTTP method to use (GET, POST, PUT, DELETE)
 * @param {Object} data - Data to send with the request (for POST/PUT)
 * @returns {Promise} - Promise with resource data
 */
const followHateoasLink = async (link, method = 'GET', data = null) => {
  if (!link) {
    throw new Error('No HATEOAS link provided');
  }
  
  try {
    const fullUrl = link;
      
    // Add request ID for tracing this specific request in logs
    const requestId = Math.random().toString(36).substring(2, 10);
    const callInfo = apiCallTracker.trackCall(fullUrl, method, requestId);
    
    // console.log(`ExamAPI [${requestId}]: Following HATEOAS link with ${method}:`, fullUrl, 
    //   data ? 'with data' : 'without data', 
    //   `(call #${callInfo.count} to this endpoint)`);
    
    // Add the call stack for debugging
    //console.log(`ExamAPI [${requestId}]: Call stack:`, new Error().stack.split('\n').slice(1, 6).join('\n'));
      let response;
    const startTime = Date.now();
    
    // Use authenticated API request instead of raw axios
    response = await apiRequest(fullUrl, {
      method: method.toUpperCase(),
      ...(data && { body: JSON.stringify(data) })
    });
    
    const endTime = Date.now();
    //console.log(`ExamAPI [${requestId}]: Response received in ${endTime - startTime}ms`, 
     // typeof response === 'object' ? `data has ${Array.isArray(response) ? response.length + ' items' : 'properties: ' + Object.keys(response).join(', ')}` : 'non-object response');
    
    return response;
  } catch (error) {
    console.error(`Error following HATEOAS link ${link} with ${method}:`, error);
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    throw error;
  }
};

/**
 * Get exam session information
 * @param {number} examId - Exam ID
 * @returns {Promise} - Promise with exam session data
 */
const getExamSession = async (examId) => {
  const requestId = Math.random().toString(36).substring(2, 10);
  const url = `/api/exam/${examId}`;
  
  try {
    apiCallTracker.trackCall(url, 'GET', requestId);
    //console.log(`ExamAPI [${requestId}]: Getting exam session with ID: ${examId}`);
      const startTime = Date.now();
    const response = await apiRequest(url);
    const duration = Date.now() - startTime;
    
    //console.log(`ExamAPI [${requestId}]: Exam session fetched in ${duration}ms`);
    return response;
  } catch (error) {
    //console.error(`ExamAPI [${requestId}]: Error getting exam session:`, error);
    throw error;
  }
};

/**
 * Get questions for an exam session
 * @param {number} examId - Exam ID
 * @returns {Promise} - Promise with questions data
 */
const getSessionQuestions = async (examId) => {
  const requestId = Math.random().toString(36).substring(2, 10);
  const url = `/api/exam/${examId}/questions`;
  
  try {
    apiCallTracker.trackCall(url, 'GET', requestId);
    // console.log(`ExamAPI [${requestId}]: Getting questions for exam session ${examId}`);
      const startTime = Date.now();
    const response = await apiRequest(url);
    const duration = Date.now() - startTime;
    
    // console.log(`ExamAPI [${requestId}]: Questions fetched in ${duration}ms, count: ${Array.isArray(response) ? response.length : 'unknown'}`);
    return response;
  } catch (error) {
    console.error(`ExamAPI [${requestId}]: Error getting session questions:`, error);
    throw error;
  }
};

/**
 * End an exam session
 * @param {string} endLink - HATEOAS link to end the exam
 * @param {number} [timeSpent=0] - Total time spent on the exam in seconds
 * @returns {Promise} - Promise with end result
 */
const endExam = async (endLink, timeSpent = 0) => {
  const requestBody = { TimeSpent: timeSpent };
  return followHateoasLink(endLink, 'POST', requestBody);
};

/**
 * Get answers for a question - uses link from HATEOAS to fetch answers
 * @param {string} answersLink - HATEOAS link for the question's answers
 * @returns {Promise} - Promise with answers data
 */
const getQuestionAnswers = async (answersLink) => {
  return followHateoasLink(answersLink);
};

/**
 * Submit an answer for a question
 * @param {string} submitLink - HATEOAS link for submitting the answer
 * @param {Object} answerData - Answer data to submit
 * @returns {Promise} - Promise with submission result
 */
const submitAnswer = async (submitLink, answerData) => {
  return followHateoasLink(submitLink, 'POST', answerData);
};

/**
 * Save the current state of a question (e.g., when flagged or skipped)
 * @param {string} saveLink - HATEOAS link for saving the question state
 * @param {Object} stateData - State data to save
 * @returns {Promise} - Promise with save result
 * @deprecated Use submitAnswer instead which uses POST as required by the API
 */
const saveQuestionState = async (saveLink, stateData) => {
  console.warn('saveQuestionState is deprecated. Use submitAnswer instead which uses POST as required by the API');
  return followHateoasLink(saveLink, 'POST', stateData); // Changed to POST to match backend API
};

/**
 * Get existing feedback for a question
 * @param {number} questionId - The ID of the question
 * @returns {Promise} - Promise with existing feedback data
 */
const getQuestionFeedback = async (questionId) => {
  try {
    const requestId = Math.random().toString(36).substring(2, 10);
    const url = `/api/question/${questionId}/feedback`;
    
    apiCallTracker.trackCall(url, 'GET', requestId);
    // console.log(`ExamAPI [${requestId}]: Getting existing feedback for question ${questionId}`);
    
    const startTime = Date.now();
    const response = await apiRequest(url, {
      method: 'GET'
    });
    const duration = Date.now() - startTime;
    
    // console.log(`ExamAPI [${requestId}]: Feedback retrieved successfully in ${duration}ms`);
    return response;
  } catch (error) {
    // If feedback doesn't exist (404), return null instead of throwing
    if (error.response?.status === 404) {
      // console.log(`ExamAPI: No existing feedback found for question ${questionId}`);
      return null;
    }
    console.error('Error getting question feedback:', error);
    throw error;
  }
};

/**
 * Submit feedback for a question
 * @param {Object} feedbackData - The feedback data to submit
 * @param {number} feedbackData.questionId - The ID of the question
 * @param {string} feedbackData.feedbackText - The feedback text
 * @param {number} [feedbackData.examId] - Optional exam ID
 * @param {number} [feedbackData.sessionId] - Optional session ID
 * @returns {Promise} - Promise with submission result
 */
const submitFeedback = async (feedbackData) => {
  try {
    const requestId = Math.random().toString(36).substring(2, 10);
    const url = `/api/question/${feedbackData.questionId}/feedback`;
    
    // Prepare payload according to backend API contract
    const payload = {
      Feedback: feedbackData.feedbackText
    };
    
    apiCallTracker.trackCall(url, 'POST', requestId);
    // console.log(`ExamAPI [${requestId}]: Submitting feedback for question ${feedbackData.questionId}:`, payload);
    
    const startTime = Date.now();
    const response = await apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    const duration = Date.now() - startTime;
    
    // console.log(`ExamAPI [${requestId}]: Feedback submitted successfully in ${duration}ms`);
    return response;
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

/**
 * Start a new exam session
 * @param {Object} sessionConfig - Configuration for the new session
 * @returns {Promise} - Promise with session data
 */
const startExamSession = async (sessionConfig) => {
  return startExam(sessionConfig);
};

// Define the API object
const examApi = {
  setAuthHeader,
  startExam,
  startExamSession, // Added the missing function
  getExamSession,
  getExamSessionById: getExamSession, // Alias for getExamSessionById
  getSessionQuestions,
  followHateoasLink,
  submitAnswer,
  getQuestionFeedback,
  submitFeedback,
  endExam,
  // Additional utility to check and fix HATEOAS links in questions
  ensureQuestionHasAnswersLink(question) {
    if (!question) return question;
    
    // Clone the question to avoid mutating the original
    const enhancedQuestion = {...question};
    
    // Initialize _links if it doesn't exist
    if (!enhancedQuestion._links) {
      enhancedQuestion._links = {};
    }
    
    // Add answers link if it doesn't exist
    if (!enhancedQuestion._links.answers && enhancedQuestion.id) {
      enhancedQuestion._links.answers = {
        href: `/api/question/${enhancedQuestion.id}/answers`,
        rel: 'answers',
        type: 'GET'
      };
      // console.log(`ExamAPI: Added missing answers link for question ${enhancedQuestion.id}`);
    }
    
    return enhancedQuestion;
  }
};

// Enhance the API with debugging capabilities
const enhancedApi = debugExamApi.wrapApiCalls(examApi);

// Add debug accessor
enhancedApi.debug = debugExamApi.getTracker();

export default enhancedApi;
